/**
 * The auth endpoints (docs/specs/accounts.md, section 3), and nothing else. Every request and
 * response shape of the auth contract is in this one file, so a change on the server side is a
 * change here and nowhere else. The server sets and clears the login cookie; this module never
 * sees it.
 *
 * These calls do not go through api.ts's 401 handling on purpose. A 401 from `login` means a wrong
 * password, not an expired login, and the caller must tell the two apart.
 */
import { MUTATING_HEADER, apiErrorFrom } from '../lib/http.ts';

export type Role = 'user' | 'admin';

/**
 * The account, as `me`, `login`, and `setup` all return it. `monthly_run_quota` is null for an
 * account with no limit.
 */
export interface Me {
  email: string;
  role: Role;
  monthly_run_quota: number | null;
  runs_used_this_month: number;
}

/** The server's rule (spec section 2). Checked here only to save a round trip. */
export const MIN_PASSWORD_LENGTH = 10;

const JSON_POST = { ...MUTATING_HEADER, 'content-type': 'application/json' } as const;

async function post(path: string, body?: unknown): Promise<Response> {
  const res = await fetch(path, {
    method: 'POST',
    headers: body === undefined ? MUTATING_HEADER : JSON_POST,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) throw await apiErrorFrom(res);
  return res;
}

/**
 * The account in a 200 body, or null when the body is not one. Never throws: by the time this
 * runs the server has accepted the credential and set the cookie, and a body we cannot read must
 * not turn into "sign-in failed". The caller asks `me` instead.
 */
async function accountIn(res: Response): Promise<Me | null> {
  try {
    const body = (await res.json()) as Partial<Me> | null;
    if (!body || typeof body.email !== 'string') return null;
    if (body.role !== 'user' && body.role !== 'admin') return null;
    return {
      email: body.email,
      role: body.role,
      monthly_run_quota: typeof body.monthly_run_quota === 'number' ? body.monthly_run_quota : null,
      runs_used_this_month:
        typeof body.runs_used_this_month === 'number' ? body.runs_used_this_month : 0,
    };
  } catch {
    return null;
  }
}

/** 400: a field is missing. 401: wrong email or password. 429: this email is throttled. */
export async function login(email: string, password: string): Promise<Me | null> {
  return accountIn(await post('/api/auth/login', { email, password }));
}

/** 200 `{ok:true}`. The body carries nothing, so it is not read. */
export async function logout(): Promise<void> {
  await post('/api/auth/logout');
}

/** 401: no valid login cookie. */
export async function me(): Promise<Me> {
  const res = await fetch('/api/auth/me');
  if (!res.ok) throw await apiErrorFrom(res);
  return (await res.json()) as Me;
}

/**
 * 200 `{ok:true}`. 400: the current password is wrong, or the new one was refused. 401: no valid
 * login cookie, and nothing else.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await post('/api/auth/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

/**
 * Whose setup link this is. Public, and it does not use the token up. 400: the link is unknown,
 * used, or expired. It is a POST with the token in the body because a token must never be in a
 * request URL, where access logs and proxies keep it.
 */
export async function setupLookup(token: string): Promise<{ email: string }> {
  const res = await post('/api/auth/setup/lookup', { token });
  return (await res.json()) as { email: string };
}

/** 400: the link is expired or used, or the password is too short. Success also logs in. */
export async function setup(token: string, password: string): Promise<Me | null> {
  return accountIn(await post('/api/auth/setup', { token, password }));
}
